import { DOCUMENT } from "@angular/common";
import { Component, EventEmitter, Inject, OnInit, Output } from "@angular/core";
import { AppConfigService } from "@app/core";
import { ProfileModel } from "@app/core/models";
import { AccountService } from "@app/core/services/account.service";
import { AuthService } from "@app/core/services/auth.service";
import { Subscription } from "@app/models";
import { PAGE_ROUTES } from "@app/pages/pages-routes";
import {
  ChargebeePortalService,
  CurrentSubscriptionPlanService,
  PaymentMethodManagementService,
  SubscriptionPlanService
} from "@app/services";
import { CompanyDataService } from "@app/services/company-data.service";
import { Unsubscriber } from "@app/shared/components";
import { PaymentMethodGetDto, SubscriptionPlan } from "clearline-api";
import { RoleTypes } from "core-kit";
import { Observable } from "rxjs";

@Component({
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"]
})
export class TopbarComponent extends Unsubscriber implements OnInit {
  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  myPlanRedirectUrl = PAGE_ROUTES.MANAGER_PRICING_PLAN;
  user: ProfileModel;
  subscription: Subscription = null;
  element;
  configData;
  cookieValue;
  flagvalue;
  countryName;
  valueset;
  name: string;
  smallLogoUrl: string;
  fullLogoUrl: string;

  paymentMethod$: Observable<PaymentMethodGetDto>;

  profileUrl: string;
  plan: SubscriptionPlan;
  hasCmc = false;
  isAdmin = this.authService.requiredMinimumRole(RoleTypes.PartnerAdmin);
  isPartnerAdmin = this.authService.isUserInRole(RoleTypes.PartnerAdmin);
  isManager = this.authService.isUserInRole(RoleTypes.Manager);
  isLocationManager = this.authService.isUserInRole(RoleTypes.LocationManager);
  isSales = this.authService.isUserInRole(RoleTypes.Sales);
  selfServicePortalUrl = this.confService.appData.selfServicePortalUrl;
  marketingCenterUrl = this.confService.appData.marketingCenterUrl;

  listLang = [
    { text: "English", flag: "assets/images/flags/us.jpg", lang: "en" },
    { text: "Spanish", flag: "assets/images/flags/spain.jpg", lang: "es" },
    { text: "German", flag: "assets/images/flags/germany.jpg", lang: "de" },
    { text: "Italian", flag: "assets/images/flags/italy.jpg", lang: "it" },
    { text: "Russian", flag: "assets/images/flags/russia.jpg", lang: "ru" }
  ];

  openMobileMenu: boolean;

  get periodEndDate() {
    const endDate = new Date(this.subscription.endDate);
    const dd = String(endDate.getDate()).padStart(2, "0");
    const mm = String(endDate.getMonth() + 1).padStart(2, "0");
    const yyyy = endDate.getFullYear();
    return mm + "/" + dd + "/" + yyyy;
  }

  get periodEndDays() {
    const endDate = new Date(this.subscription.endDate);
    const today = new Date();
    const difference = endDate.getTime() - today.getTime();
    const days = Math.ceil(difference / (1000 * 3600 * 24));
    return days;
  }

  constructor(
    @Inject(DOCUMENT) private document: any,
    private authService: AuthService,
    private currentSubscriptionPlanService: CurrentSubscriptionPlanService,
    private subscriptionPlanService: SubscriptionPlanService,
    private accountService: AccountService,
    private confService: AppConfigService,
    private companyDataService: CompanyDataService,
    private paymentMethodManagementService: PaymentMethodManagementService,
    private chargebeePortalService: ChargebeePortalService
  ) {
    super();
  }

  ngOnInit() {
    this.openMobileMenu = false;
    this.element = document.documentElement;

    this.configData = {
      suppressScrollX: true,
      wheelSpeed: 0.3
    };

    this.paymentMethod$ = this.paymentMethodManagementService.paymentMethod$;

    this.user = this.accountService.user;
    const config = this.confService.appData;
    this.openProfileMenu();
    if (config && config.name) {
      this.name = config.name;
    } else {
      this.name = "clearline";
    }

    if (config && config.smallLogoUrl) {
      this.smallLogoUrl = config.smallLogoUrl;
    } else {
      this.smallLogoUrl = `assets/images/logo/small-${this.name}-menu-logo.svg`;
    }
    if (config && config.fullLogoUrl) {
      this.fullLogoUrl = config.fullLogoUrl;
    } else {
      this.fullLogoUrl = `assets/images/logo/full-${this.name}-menu-logo.svg`;
    }

    const identityUrl = config.identityUrl;
    const returnUrl = config.authConfig.redirectUrl;
    this.profileUrl = `${identityUrl}?returnUrl=${returnUrl}`;
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout() {
    this.authService.logout();
  }

  openProfileMenu() {
    if (!this.accountService.isUserInRole(RoleTypes.Admin) && this.accountService.user?.doneQuickSetup) {
      if (this.accountService.user?.locationId) {
        this.sub = this.subscriptionPlanService.getLocationPlan(this.accountService.user.locationId).subscribe((plan) => {
          this.plan = plan;
          this.hasCmc = plan?.options?.hasCmc;
        });
      }
      this.sub = this.companyDataService.getSubscription().subscribe((subscription: Subscription) => {
        this.subscription = subscription;
        this.currentSubscriptionPlanService.setSubscription(subscription);
      });
    }
  }

  goToProfile() {
    window.location.href = this.profileUrl;
  }

  fullscreen() {
    document.body.classList.toggle("fullscreen-enable");
    if (!document.fullscreenElement && !this.element.mozFullScreenElement && !this.element.webkitFullscreenElement) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        /* Firefox */
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        /* Chrome, Safari and Opera */
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        /* IE/Edge */
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        /* Firefox */
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        /* Chrome, Safari and Opera */
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        /* IE/Edge */
        this.document.msExitFullscreen();
      }
    }
  }

  openBillingPortal(): void {
    this.chargebeePortalService.openBillingPortal();
  }

  addPaymentMethod(paymentMethod: PaymentMethodGetDto): void {
    this.sub = this.paymentMethodManagementService.addPaymentMethod(paymentMethod).subscribe();
  }

  deletePaymentMethod(paymentMethod: PaymentMethodGetDto): void {
    this.sub = this.paymentMethodManagementService.deletePaymentMethod(paymentMethod).subscribe();
  }
}
